import type { Editor } from '@tiptap/react'
import { marked } from 'marked'
import TurndownService from 'turndown'

import {
  addCodeRule,
  addHighlightRule,
  addMentionRule,
  addStrikethroughRule,
  addTableRule,
  addTaskListRule,
  addUnderlineRule,
} from './turndown-rules.util'

const turndownConfig = {
  headingStyle: 'atx',
  hr: '---',
  bulletListMarker: '-',
  codeBlockStyle: 'fenced',
  fence: '```',
  emDelimiter: '*',
  strongDelimiter: '**',
  linkStyle: 'inlined',
} as const

const createTurndownService = (): TurndownService => {
  const turndown = new TurndownService(turndownConfig)
  addUnderlineRule(turndown)
  addStrikethroughRule(turndown)
  addTaskListRule(turndown)
  addMentionRule(turndown)
  addHighlightRule(turndown)
  addCodeRule(turndown)
  addTableRule(turndown)
  return turndown
}

let turndownInstance: TurndownService | null = null

const getTurndownService = (): TurndownService => {
  if (!turndownInstance) {
    turndownInstance = createTurndownService()
  }
  return turndownInstance
}

marked.setOptions({ gfm: true, breaks: true })

export const htmlToMarkdown = (html: string): string => {
  if (!html || html.trim() === '' || html === '<p></p>') {
    return ''
  }
  return getTurndownService().turndown(html).trim()
}

export const markdownToHtml = (markdown: string): string => {
  if (!markdown || markdown.trim() === '') {
    return ''
  }
  const result = marked.parse(markdown)
  return typeof result === 'string' ? result : ''
}

export const getMarkdownFromEditor = (editor: Editor | null): string => {
  if (!editor) return ''
  const html = editor.getHTML()
  return htmlToMarkdown(html)
}

export const setEditorFromMarkdown = (editor: Editor | null, markdown: string): void => {
  if (!editor) return
  const html = markdownToHtml(markdown)
  editor.commands.setContent(html)
}

export const isEditorEmpty = (editor: Editor | null): boolean => {
  if (!editor) return true
  return editor.isEmpty
}

export const getCharacterCount = (editor: Editor | null): number => {
  if (!editor) return 0
  return editor.storage.characterCount?.characters() ?? editor.getText().length
}

export const getWordCount = (editor: Editor | null): number => {
  if (!editor) return 0
  return (
    editor.storage.characterCount?.words() ?? editor.getText().split(/\s+/).filter(Boolean).length
  )
}
